import { useEffect, useState } from "react";
import { COLORS, Ic, ICONS } from "@/lib/theme";
import { apiGet, apiPost, apiPut, apiDelete, apiDownload, formatApiError } from "@/lib/api";

import { isPhoneApp } from "@/lib/phoneApp";

export default function Admin({ onClose }) {
  const [tab, setTab] = useState("dashboard");

  const tabs = [
    ["dashboard", "Dashboard"],
    ["members", "Members"],
    ["events", "Events"],
    ["newsletters", "Newsletters"],
    ...(isPhoneApp ? [["times", "Times"]] : []),
    ["announcements", "Announcements"],
    ["learning", "Learning"],
    ["donations", "Donations"],
  ];

  return (
    <div className="anim-slideIn" data-testid="admin-screen" style={{ position: "absolute", inset: 0, background: COLORS.cream, zIndex: 50, display: "flex", flexDirection: "column" }}>
      <div className="ls-header" style={{ padding: "16px 20px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button data-testid="admin-close" onClick={onClose} aria-label="Back" className="no-tap-highlight" style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            <Ic d={ICONS.back} color="#fff" size={26} />
          </button>
          <div style={{ flex: 1 }}>
            <div className="font-heading" style={{ fontWeight: 800, fontSize: 20, color: "#fff" }}>Admin Portal</div>
            <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, fontWeight: 700 }}>LinkShul administration</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 14, overflowX: "auto", paddingBottom: 4 }} className="ls-scroll">
          {tabs.map(([k, l]) => (
            <button
              key={k}
              data-testid={`admin-tab-${k}`}
              onClick={() => setTab(k)}
              className="no-tap-highlight"
              style={{
                padding: "7px 14px", borderRadius: 20, border: "none", cursor: "pointer",
                fontWeight: 800, fontSize: 12, fontFamily: "'Nunito', sans-serif",
                background: tab === k ? "#fff" : "rgba(255,255,255,0.18)",
                color: tab === k ? COLORS.purple : "#fff",
                whiteSpace: "nowrap", flexShrink: 0,
              }}
            >{l}</button>
          ))}
        </div>
      </div>
      <div className="ls-scroll" style={{ flex: 1, overflowY: "auto", padding: "16px 18px 30px" }}>
        {tab === "dashboard" && <Dashboard />}
        {tab === "members" && <Members />}
        {tab === "events" && <ContentManager coll="events" label="Events" fields={isPhoneApp ? ["title","date_iso","date_display","location","description"] : ["title","date_display","location","description"]} />}
        {tab === "times" && <PhoneSchedule />}
        {tab === "newsletters" && isPhoneApp && <PhoneNewsletterUpload />}
        {tab === "newsletters" && !isPhoneApp && <ContentManager coll="newsletters" label="Newsletters" fields={["title","excerpt","body","published_at"]} />}
        {tab === "announcements" && <ContentManager coll="announcements" label="Announcements" fields={["title","body","priority","posted_at"]} />}
        {tab === "learning" && <ContentManager coll="learning" label="Learning" fields={["title","when","location","teacher","description"]} />}
        {tab === "donations" && <Donations />}
      </div>
    </div>
  );
}

function PhoneSchedule() {
  const [schedule, setSchedule] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    apiGet("/services?phone=true").then((data) => {
      setSchedule(Object.fromEntries(["weekday", "shabbos"].map((key) =>
        [key, data[key].map(({ name, time }) => `${time} | ${name}`).join("\n")]
      )));
    }).catch((error) => setMessage(formatApiError(error)));
  }, []);

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const payload = Object.fromEntries(Object.entries(schedule).map(([key, value]) => [
        key, value.split("\n").filter((line) => line.trim()).map((line) => {
          const match = line.trim().match(/^(\d{2}:\d{2})\s*\|\s*(.+)$/);
          if (!match) throw new Error("Use HH:MM | Service name for each line.");
          return { time: match[1], name: match[2] };
        }),
      ]));
      await apiPut("/admin/phone-services", payload);
      setMessage("Times saved.");
    } catch (error) {
      setMessage(formatApiError(error));
    } finally {
      setBusy(false);
    }
  }

  return <form onSubmit={save}>
    <p>Enter one service per line, using HH:MM | Service name.</p>
    {schedule && <>
      {[["weekday", "Weekly Services"], ["shabbos", "Shabbat Services"]].map(([key, label]) =>
        <label key={key}>{label}<textarea className="ls-input" rows={6} value={schedule[key]} onChange={(event) => setSchedule({ ...schedule, [key]: event.target.value })} /></label>
      )}
      <button className="ls-primary-btn" disabled={busy}>Save times</button>
    </>}
    <p role="status">{message}</p>
  </form>;
}

function PhoneNewsletterUpload() {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function upload(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    setMessage("");
    try {
      await apiPost(`/admin/phone-newsletters?title=${encodeURIComponent(data.get("title"))}`, data.get("pdf"));
      form.reset();
      setMessage("Newsletter published.");
    } catch (error) {
      setMessage(formatApiError(error));
    } finally {
      setBusy(false);
    }
  }

  return <form onSubmit={upload}>
    <label>Newsletter title<input className="ls-input" name="title" required maxLength={200} /></label>
    <label>Newsletter PDF<input className="ls-input" name="pdf" type="file" accept="application/pdf,.pdf" required /></label>
    <button className="ls-primary-btn" disabled={busy}>Publish newsletter</button>
    <p role="status">{message}</p>
  </form>;
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  useEffect(() => { apiGet("/admin/stats").then(setStats).catch(() => {}); apiGet("/health").then(setHealth).catch(() => {}); }, []);
  if (!stats) return <div style={{ color: COLORS.muted, fontWeight: 700 }}>Loading…</div>;
  const cards = [
    ["Total Members", stats.total_members, COLORS.purple],
    ["Approved", stats.approved_members, "#2d8a4d"],
    ["Pending", stats.pending_members, COLORS.gold],
    ["Donations", stats.total_donations, COLORS.plum],
    ["Raised", `R ${stats.total_donated_zar.toLocaleString()}`, COLORS.purple],
  ];
  return (
    <div>
      <div className="font-heading" style={{ fontWeight: 800, fontSize: 22, color: COLORS.ink, marginBottom: 14 }}>Dashboard</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {cards.map(([label, val, color]) => (
          <div key={label} className="ls-card" style={{ padding: 16 }} data-testid={`admin-stat-${label.toLowerCase().replace(/ /g,'-')}`}>
            <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 800, letterSpacing: 1 }}>{label.toUpperCase()}</div>
            <div className="font-heading" style={{ fontWeight: 800, fontSize: 24, color, marginTop: 6 }}>{val}</div>
          </div>
        ))}
      </div>
      {health && <div className="ls-card" style={{ padding: 14, marginTop: 14 }}>
        <div style={{ fontWeight: 800, color: COLORS.ink }}>System checks</div>
        <div style={{ marginTop: 7, fontSize: 13, fontWeight: 700, color: health.ai_configured ? "#2d8a4d" : "#a02040" }}>AI: {health.ai_configured ? `Configured (${health.ai_model || "model configured"})` : "NOT CONFIGURED"}</div>
        <div style={{ marginTop: 3, fontSize: 13, fontWeight: 700, color: health.peach_configured ? "#2d8a4d" : COLORS.gold }}>Payments: {health.peach_configured ? "Peach Payments configured" : "Sandbox / credentials missing"}</div>
        <div style={{ marginTop: 3, fontSize: 13, fontWeight: 700, color: health.email_configured ? "#2d8a4d" : COLORS.gold }}>Email: {health.email_configured ? "SMTP configured" : "SMTP not configured"}</div>
      </div>}
    </div>
  );
}

function Members() {
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const data = await apiGet(tab === "all" ? "/admin/members" : `/admin/members?status=${tab}`);
    setItems(data.members || []);
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = search.trim().toLowerCase();
  const filtered = items.filter((member) => [member.first_name, member.last_name, member.email, member.phone].filter(Boolean).join(" ").toLowerCase().includes(query));

  const approve = async (id) => {
    setBusy(true);
    try { await apiPost(`/admin/members/${id}/approve`); await load(); } finally { setBusy(false); }
  };
  const suspend = async (id) => {
    setBusy(true);
    try { await apiPost(`/admin/members/${id}/suspend`); await load(); } finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {[["all","Master Member List"],["pending","Pending"],["approved","Approved"],["suspended","Suspended"]].map(([k,l]) => (
          <button
            key={k}
            data-testid={`admin-members-${k}`}
            onClick={() => setTab(k)}
            className="no-tap-highlight"
            style={{ padding: "8px 14px", borderRadius: 12, border: "none", cursor: "pointer",
              fontWeight: 800, fontSize: 12, background: tab === k ? COLORS.purple : COLORS.lilac, color: tab === k ? "#fff" : COLORS.purple }}
          >{l}</button>
        ))}
        </div>
        <button className="ls-primary-btn" onClick={() => apiDownload("/admin/export/members.xlsx", "linkshul-members.xlsx")} style={{ padding: "8px 12px", fontSize: 12 }}>Export XLSX</button>
      </div>
      {<input className="ls-input" type="search" aria-label="Search members" placeholder="Search by name, email or cellphone" value={search} onChange={(event) => setSearch(event.target.value)} style={{ marginBottom: 12 }} />}
      {filtered.length === 0 && <div style={{ color: COLORS.muted, fontWeight: 600, padding: 20, textAlign: "center" }}>No matching members.</div>}
      {filtered.map((m) => {
        const digits = (m.whatsapp || m.phone || "").replace(/\D/g, "");
        const whatsappNumber = digits.startsWith("0") ? "27" + digits.slice(1) : digits;
        const approvalMessage = encodeURIComponent("Welcome to LinkShul! Your membership has been approved. You can now sign in at https://linkshul.online.");
        return (
        <div key={m.id} className="ls-card" data-testid={`admin-member-${m.id}`} style={{ padding: 14, marginBottom: 10, display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: COLORS.lilac, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Ic d={ICONS.user} color={COLORS.plum} size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>{m.first_name} {m.last_name}</div>
            <div style={{ fontSize: 12, color: COLORS.muted, fontWeight: 600 }}>{m.email}</div>
            {m.phone && <div style={{ fontSize: 11.5, color: COLORS.muted, fontWeight: 600 }}>{m.phone}</div>}
            <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 600 }}>{[m.hebrew_name, isPhoneApp ? null : m.family_name, m.status].filter(Boolean).join(" · ")}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {m.status !== "approved" && (
              <button data-testid={`admin-approve-${m.id}`} onClick={() => approve(m.id)} disabled={busy} className="no-tap-highlight" style={{ padding: "6px 12px", borderRadius: 12, border: "none", background: "#2d8a4d", color: "#fff", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>Approve</button>
            )}
            {isPhoneApp && m.status === "approved" && whatsappNumber.length >= 10 && (
              <a href={`https://wa.me/${whatsappNumber}?text=${approvalMessage}`} target="_blank" rel="noopener noreferrer">Notify on WhatsApp</a>
            )}
            {m.status !== "suspended" && (
              <button data-testid={`admin-suspend-${m.id}`} onClick={() => suspend(m.id)} disabled={busy} className="no-tap-highlight" style={{ padding: "6px 12px", borderRadius: 12, border: `1px solid #a02040`, background: "#fff", color: "#a02040", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>Suspend</button>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );
}

function ContentManager({ coll, label, fields }) {
  const endpoint = coll === "events" ? "/events" : `/${coll}`;
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const data = await apiGet(endpoint);
    setItems(data[coll] || []);
  };
  useEffect(() => { load(); }, [coll]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setError(""); setBusy(true);
    try {
      if (isPhoneApp && coll === "events" && !form.date_iso) throw new Error("Choose the event date and time.");
      await apiPost(`/admin/${coll}`, form);
      setForm({}); setShowForm(false); await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await apiDelete(`/admin/${coll}/${id}`);
    await load();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div className="font-heading" style={{ fontWeight: 800, fontSize: 20, color: COLORS.ink }}>{label}</div>
        <button data-testid={`admin-${coll}-add`} className="ls-primary-btn" onClick={() => setShowForm(!showForm)} style={{ padding: "8px 16px", fontSize: 13 }}>{showForm ? "Cancel" : "+ New"}</button>
      </div>
      {showForm && (
        <div className="ls-card" style={{ padding: 14, marginBottom: 14 }}>
          {fields.map((f) => f === "date_iso" ? (
            <label key={f}>Event date and time (Johannesburg)
              <input type="datetime-local" className="ls-input" value={form.date_iso || ""} onChange={(event) => setForm({ ...form, date_iso: event.target.value })} />
            </label>
          ) : (
            <textarea
              key={f}
              data-testid={`admin-${coll}-field-${f}`}
              placeholder={f}
              value={form[f] || ""}
              onChange={(e) => setForm({ ...form, [f]: e.target.value })}
              className="ls-input"
              style={{ marginBottom: 8, minHeight: f === "body" || f === "description" ? 100 : 44, resize: "vertical", fontFamily: "'Nunito', sans-serif" }}
            />
          ))}
          {error && <div style={{ color: "#a02040", fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{error}</div>}
          <button data-testid={`admin-${coll}-save`} onClick={create} disabled={busy} className="ls-primary-btn" style={{ width: "100%" }}>{busy ? "Saving…" : "Save"}</button>
        </div>
      )}
      {items.map((it) => (
        <div key={it.id} className="ls-card" data-testid={`admin-${coll}-item-${it.id}`} style={{ padding: 14, marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 14 }}>{it.title}</div>
              {(it.date_display || it.when || it.published_at || it.posted_at) && (
                <div style={{ fontSize: 12, color: COLORS.purple, fontWeight: 700, marginTop: 2 }}>{it.date_display || it.when || it.published_at || it.posted_at}</div>
              )}
              {(it.location || it.teacher) && (
                <div style={{ fontSize: 12, color: COLORS.muted, fontWeight: 600, marginTop: 2 }}>{it.location || it.teacher}</div>
              )}
              {(it.excerpt || it.description || it.body) && (
                <div style={{ fontSize: 12.5, color: COLORS.ink, fontWeight: 600, marginTop: 6, lineHeight: 1.4 }}>{(it.excerpt || it.description || it.body || "").slice(0, 120)}…</div>
              )}
            </div>
            <button data-testid={`admin-${coll}-delete-${it.id}`} onClick={() => del(it.id)} className="no-tap-highlight" style={{ background: "none", border: "none", color: "#a02040", fontWeight: 800, cursor: "pointer", fontSize: 12 }}>Delete</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Donations() {
  const [items, setItems] = useState([]);
  useEffect(() => { apiGet("/admin/donations").then((d) => setItems(d.donations || [])).catch(() => {}); }, []);
  const total = items.reduce((sum, d) => (["sandbox_completed","success"].includes(d.status) ? sum + (d.amount || 0) : sum), 0);
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}><button className="ls-primary-btn" onClick={() => apiDownload("/admin/export/donations.xlsx", "linkshul-donations.xlsx")} style={{ padding: "8px 12px", fontSize: 12 }}>Export Donations XLSX</button></div>
      <div className="ls-card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 800, letterSpacing: 1 }}>TOTAL RECEIVED</div>
        <div className="font-heading" style={{ fontWeight: 800, fontSize: 26, color: COLORS.purple, marginTop: 4 }}>R {total.toLocaleString()}</div>
        <div style={{ fontSize: 12, color: COLORS.muted, fontWeight: 700, marginTop: 4 }}>{items.length} total donations</div>
      </div>
      {items.map((d) => (
        <div key={d.id} className="ls-card" style={{ padding: 12, marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 14 }}>R {d.amount}</div>
              <div style={{ fontSize: 12, color: COLORS.purple, fontWeight: 700 }}>{d.category}</div>
              <div style={{ fontSize: 11.5, color: COLORS.muted, fontWeight: 600 }}>{d.donor_name || d.donor_email || "—"}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: d.status?.includes("completed") || d.status?.includes("success") ? "#2d8a4d" : COLORS.muted }}>
                {d.status}
              </div>
              <div style={{ fontSize: 10.5, color: COLORS.muted, fontWeight: 600, marginTop: 2 }}>{new Date(d.created_at).toLocaleDateString()}</div>
            </div>
          </div>
        </div>
      ))}
      {items.length === 0 && <div style={{ color: COLORS.muted, fontWeight: 600, padding: 20, textAlign: "center" }}>No donations yet.</div>}
    </div>
  );
}
